import { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import MealBox from './MealBox';
import { useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Card from '@material-ui/core/Card';
import { useFontSize } from '../../../contexts/FontSizeContext';

const daylist = ['sun', 'mon', 'tue', 'wed', 'thur', 'fri', 'sat'];
const weekDayNum = new Date().getDay();
const curWeekday = daylist[weekDayNum];

const useStyles = makeStyles((theme) => {
    return {
        wrapper: {
            width: '100%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
        },
        tabsWrapper: {
            width: '100%',
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 100,
        },
        tabs: {
            minHeight: '55px',
            '& .MuiTabs-indicator': {
                height: '3px',
                backgroundColor: theme.palette.primary.main,
            },
        },
        tab: {
            fontWeight: '500',
            minWidth: 'auto',
            padding: '12px 20px',
            textTransform: 'none',
            color: '#666',
            borderRadius: '8px 8px 0 0', // Rounded top corners
            transition: 'all 0.2s ease-in-out', // Smooth transition
            '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: '600',
                backgroundColor: 'rgba(230, 81, 0, 0.1)', // Light orange background
            },
        },
        itemWrapper: {
            overflowY: 'auto',
            overflowX: 'hidden',
            maxHeight: 'calc(100vh - 120px)',
            padding: '0 15px 15px 15px',
            width: '100%',
            boxSizing: 'border-box',
        },
        bottomSpacer: {
            height: '100px',
            width: '100%',
        },
        sectionHeader: {
            marginTop: '30px',
            marginBottom: '20px',
            paddingLeft: '15px',
            paddingTop: '10px',
            paddingBottom: '10px',
            fontWeight: '600',
            color: theme.palette.primary.main,
            backgroundColor: 'rgba(230, 81, 0, 0.05)',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: '0 8px 8px 0',
            scrollMarginTop: '70px',
        },
        firstSectionHeader: {
            marginTop: '15px',
            marginBottom: '20px',
            paddingLeft: '15px',
            paddingTop: '10px',
            paddingBottom: '10px',
            fontWeight: '600',
            color: theme.palette.primary.main,
            backgroundColor: 'rgba(230, 81, 0, 0.05)',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: '0 8px 8px 0',
            scrollMarginTop: '70px',
        },
        sectionContent: {
            marginBottom: '20px',
        },
        sectionCard: {
            marginTop: '10px',
            marginBottom: '10px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
        },
        mealItemWrapper: {
            '&:not(:last-child)': {
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
        },
        none: {
            display: 'none',
        },
    }
});



// Provide layout for 1st and 2nd menu
const MenuWrapper = (props) => {
    const classes = useStyles(props);
    const { getFontSize, updateKey } = useFontSize();
    const [subCatList, setSubCatList] = useState([]);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [allMealsBySubCat, setAllMealsBySubCat] = useState({});
    const sectionRefs = useRef({});
    const itemWrapperRef = useRef(null);
    const isScrollingFromTab = useRef(false);

    // Dynamic font sizes
    const sectionHeaderFontSize = getFontSize('sectionHeader');
    const tabFontSize = getFontSize('tabText');

    const {
        menu
    } = useSelector((store) => store.menuReducer);

    useEffect(() => {
        console.log(menu);
        if (menu.mealSubCategoryList) {
            const filteredSubCatList = menu.mealSubCategoryList.filter(
                subCatItem => typeof subCatItem.showMenuToClient === 'undefined' ||
                subCatItem.showMenuToClient === true
            );
            setSubCatList(filteredSubCatList);
        }
    }, [menu]);

    useEffect(() => {
        const {entireMap, mealCategoryList} = menu;

        if (entireMap && mealCategoryList && subCatList.length > 0) {
            const mealsBySubCat = {};

            // Get the first main category (assuming we're using the first one)
            const mainCat = mealCategoryList[0];
            if (mainCat) {
                subCatList.forEach(subCat => {
                    if (
                        entireMap[mainCat.mealCategoryId] &&
                        entireMap[mainCat.mealCategoryId][subCat.mealSubCategoryId] &&
                        entireMap[mainCat.mealCategoryId][subCat.mealSubCategoryId].meals
                    ) {
                        mealsBySubCat[subCat.mealSubCategoryId] = {
                            subCat: subCat,
                            meals: entireMap[mainCat.mealCategoryId][subCat.mealSubCategoryId].meals
                        };
                    }
                });
            }

            setAllMealsBySubCat(mealsBySubCat);
        }
    }, [menu, subCatList]);

    // Set up scroll listener to update active tab based on visible section
    useEffect(() => {
        const container = itemWrapperRef.current;
        if (!container || subCatList.length === 0) return;

        const handleScroll = () => {
            // Don't update tab if user just clicked a tab (to avoid conflicts)
            if (isScrollingFromTab.current) return;

            const containerScrollTop = container.scrollTop;
            const triggerPoint = containerScrollTop + 100; // 100px from top of container

            // Find which section the trigger point is currently in
            let activeIndex = 0;

            for (let i = subCatList.length - 1; i >= 0; i--) {
                const subCat = subCatList[i];
                const sectionElement = sectionRefs.current[subCat.mealSubCategoryId];
                if (!sectionElement) continue;

                const sectionTop = sectionElement.offsetTop;

                // If trigger point is past this section's start, this is the active section
                if (triggerPoint >= sectionTop) {
                    activeIndex = i;
                    break;
                }
            }

            setSelectedTabIndex(activeIndex);
        };

        container.addEventListener('scroll', handleScroll);

        // Initial check
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [subCatList]);

    const handleTabChange = (_, newValue) => {
        setSelectedTabIndex(newValue);

        // Set flag to prevent scroll listener from interfering
        isScrollingFromTab.current = true;

        // Scroll to the corresponding section within the itemWrapper container
        const subCat = subCatList[newValue];
        if (subCat && sectionRefs.current[subCat.mealSubCategoryId] && itemWrapperRef.current) {
            const targetElement = sectionRefs.current[subCat.mealSubCategoryId];
            const container = itemWrapperRef.current;

            // Calculate the position relative to the container
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();
            const scrollTop = container.scrollTop;
            const targetPosition = targetRect.top - containerRect.top + scrollTop - 20; // 20px offset for spacing

            container.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });

            // Reset flag after scroll animation completes
            setTimeout(() => {
                isScrollingFromTab.current = false;
            }, 1000); // Give enough time for smooth scroll to complete
        }
    };

    const setSectionRef = (subCatId, ref) => {
        if (ref) {
            sectionRefs.current[subCatId] = ref;
        }
    };

    return (
        <div className={classes.wrapper}>
            {/* Material-UI Tabs for subcategory navigation */}
            <div className={classes.tabsWrapper}>
                <Tabs
                    value={selectedTabIndex}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    {subCatList.map((item) => (
                        <Tab
                            key={item.mealSubCategoryId}
                            label={item.name}
                            className={classes.tab}
                            style={{ fontSize: tabFontSize }}
                        />
                    ))}
                </Tabs>
            </div>

            {/* All meals displayed in sections */}
            <div ref={itemWrapperRef} className={classes.itemWrapper}>
                {subCatList.map((subCat, index) => {
                    const subCatData = allMealsBySubCat[subCat.mealSubCategoryId];
                    if (!subCatData || !subCatData.meals) return null;

                    const filteredMeals = subCatData.meals.filter((meal) => {
                        // Filter by weekday
                        if (meal.weekday !== 'unlimited' && meal.weekday !== curWeekday) {
                            return false;
                        }
                        // Filter by showMenuToClient
                        if (meal.showMenuToClient && meal.showMenuToClient === 'hide') {
                            return false;
                        }
                        return true;
                    });

                    if (filteredMeals.length === 0) return null;

                    return (
                        <div key={subCat.mealSubCategoryId} className={classes.sectionContent}>
                            {/* Section header */}
                            <Typography
                                ref={(ref) => setSectionRef(subCat.mealSubCategoryId, ref)}
                                className={index === 0 ? classes.firstSectionHeader : classes.sectionHeader}
                                variant="inherit"
                                style={{
                                    fontSize: sectionHeaderFontSize,
                                    display: 'block'
                                }}
                            >
                                {subCat.name}
                            </Typography>

                            {/* Card containing all meals in this section */}
                            <Card className={classes.sectionCard}>
                                {filteredMeals.map((meal) => (
                                    <div key={meal.mealId} className={classes.mealItemWrapper}>
                                        <MealBox
                                            meal={meal}
                                            disabled={meal.outOfStock}
                                            inCard={true}
                                        />
                                    </div>
                                ))}
                            </Card>
                        </div>
                    );
                })}

                {/* Bottom spacer for shopping cart button */}
                <div className={classes.bottomSpacer}></div>
            </div>
        </div>
    );
};

export default MenuWrapper;